// Time grid constants for the Agenda Timeline
export const SLOT_START_HOUR = 8;
export const SLOT_END_HOUR = 20;
export const SLOT_MINUTES = 30; // 30-min slots
export const SLOT_HEIGHT_PX = 56; // visual height per 30-min slot
export const TOTAL_SLOTS = ((SLOT_END_HOUR - SLOT_START_HOUR) * 60) / SLOT_MINUTES;

export const PROGRESS_THRESHOLDS = {
    safe: 0.8, // <= 80% green
    warning: 1.0, // > 100% red
};

// O backend fala scheduled/confirmed/in_progress/done/cancelled/refused; o
// estado local do front fala agendado/em-andamento/concluido/cancelado. Depois
// de um syncTenantData() o appointment volta com o vocabulário do backend —
// normaliza pro do front pra os dois convergirem (senão, ex.: um booking
// confirmado sincroniza como "scheduled" e o card perde o botão "Iniciar").
export const normalizeApptStatus = (s) => {
    switch (s) {
        case "scheduled":
        case "confirmed":
            return "agendado";
        case "in_progress":
        case "started":
            return "em-andamento";
        case "done":
        case "completed":
        case "finished":
            return "concluido";
        case "cancelled":
        case "canceled":
        case "refused":
        case "no_show":
            return "cancelado";
        case undefined:
        case null:
        case "":
            return "agendado";
        default:
            return s;
    }
};

export const isCancelledStatus = (s) => normalizeApptStatus(s) === "cancelado";

// Build all 30-min slot labels for the day grid (e.g. "08:00", "08:30", ...)
export const buildSlotLabels = () => {
    const labels = [];
    for (let i = 0; i < TOTAL_SLOTS; i++) {
        const totalMin = i * SLOT_MINUTES;
        const h = SLOT_START_HOUR + Math.floor(totalMin / 60);
        const m = totalMin % 60;
        labels.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
    }
    return labels;
};

// Convert a Date into slot index (0-based) within the day grid.
export const dateToSlotIndex = (date) => {
    const d = date instanceof Date ? date : new Date(date);
    const totalMin = (d.getHours() - SLOT_START_HOUR) * 60 + d.getMinutes();
    return totalMin / SLOT_MINUTES;
};

// How many slots a service of N minutes occupies (rounded up)
export const minutesToSlots = (durationMin) => Math.max(1, Math.ceil(durationMin / SLOT_MINUTES));

// Compute progress (0..1+) for an in-progress appointment
export const computeProgress = (appointment, now = Date.now()) => {
    if (!appointment.started_at) return 0;
    const duration = (appointment.duration_min || 30) * 60000;
    if (duration <= 0) return 0;
    return (now - appointment.started_at) / duration;
};

// Map progress to a color band (semantic)
export const progressBand = (progress) => {
    if (progress > PROGRESS_THRESHOLDS.warning) return "danger";
    if (progress >= PROGRESS_THRESHOLDS.safe) return "warning";
    return "safe";
};

// Returns true if two appointments overlap in time on the same professional
export const overlaps = (a, b) => {
    if (a.professionalId !== b.professionalId) return false;
    if (a.id === b.id) return false;
    const aEnd = a.estimated_end || a.date + (a.duration_min || 30) * 60000;
    const bEnd = b.estimated_end || b.date + (b.duration_min || 30) * 60000;
    return a.date < bEnd && b.date < aEnd;
};

// Filter appointments for a given day (local time)
export const isSameDay = (ts, ref) => {
    const a = new Date(ts);
    const b = ref instanceof Date ? ref : new Date(ref);
    return (
        a.getFullYear() === b.getFullYear() &&
        a.getMonth() === b.getMonth() &&
        a.getDate() === b.getDate()
    );
};

// Status palette (consistent across all modules)
export const STATUS_PALETTE = {
    agendado: {
        label: "Agendado",
        chipBg: "bg-amber-500/10",
        chipBorder: "border-amber-500/40",
        chipText: "text-amber-200",
        accent: "#d4af37",
    },
    "em-andamento": {
        label: "Em andamento",
        chipBg: "bg-sky-500/15",
        chipBorder: "border-sky-500/50",
        chipText: "text-sky-200",
        accent: "#38bdf8",
    },
    concluido: {
        label: "Concluído",
        chipBg: "bg-emerald-500/10",
        chipBorder: "border-emerald-500/40",
        chipText: "text-emerald-200",
        accent: "#34d399",
    },
    cancelado: {
        label: "Cancelado",
        chipBg: "bg-zinc-500/10",
        chipBorder: "border-zinc-500/40",
        chipText: "text-zinc-300",
        accent: "#a1a1aa",
    },
    awaiting_confirmation: {
        label: "Aguardando confirmação",
        chipBg: "bg-amber-400/20",
        chipBorder: "border-amber-300",
        chipText: "text-amber-100",
        accent: "#fbbf24",
    },
};

export const formatDuration = (mins) => {
    if (!mins) return "—";
    if (mins < 60) return `${mins} min`;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return m === 0 ? `${h}h` : `${h}h ${m}min`;
};
